import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { qk } from '../../lib/query'
import { Icon } from '../icons'

/**
 * Nova conversa de WhatsApp a partir de um número.
 *
 * Não envia nada: abre a conversa e entrega o agente ao compositor de sempre. A primeira
 * mensagem passa pelo mesmo caminho de qualquer outra — validação, registro, envio de imagem —
 * em vez de um campo de texto paralelo aqui que faria tudo isso pela metade.
 */
export function NewConversation({ onClose, onOpened }: { onClose: () => void; onOpened: (id: string) => void }) {
  const { data: contas = [] } = useQuery({ queryKey: qk.channelAccounts(), queryFn: api.channelAccounts })

  // Só as que conseguem mandar agora. Oferecer uma conta desconectada é oferecer um erro.
  const whats = contas.filter((c) => c.channelType === 'whatsapp_qr' && c.connectionStatus === 'connected')

  const [contaId, setContaId] = useState<string>('')
  const [phone, setPhone] = useState('')
  const [name, setName] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const conta = contaId || whats[0]?.id || ''

  async function abrir(e: React.FormEvent) {
    e.preventDefault()
    if (!conta || !phone.trim() || busy) return
    setBusy(true)
    setErro(null)
    try {
      const r = await api.startConversation({ channelAccountId: conta, phone: phone.trim(), name: name.trim() || undefined })
      if (r.splitRisk) {
        // Raro — o WhatsApp devolve o LID de quase todo número. Mas quando não devolve, a primeira
        // resposta do cliente pode chegar numa conversa separada, e o agente precisa saber disso
        // antes de estranhar duas conversas com a mesma pessoa.
        setAviso('Conversa aberta, mas o WhatsApp não informou o identificador interno deste número. Se a resposta do cliente aparecer numa conversa separada, é por isso.')
        onOpened(r.conversationId)
        setBusy(false)
        return
      }
      onOpened(r.conversationId)
      onClose()
    } catch (err) {
      setErro(err instanceof ApiError && err.detail ? err.detail : 'Não foi possível abrir a conversa.')
      setBusy(false)
    }
  }

  return (
    <>
      <div className="modal-back" onClick={onClose} />
      <div className="modal" role="dialog" aria-modal="true" aria-label="Nova conversa">
        <div className="modal-h">
          <Icon name="chat" size={15} />
          Nova conversa no WhatsApp
          <button className="icon-btn" onClick={onClose} aria-label="Fechar">
            <Icon name="x" size={14} />
          </button>
        </div>
        <form className="modal-b" onSubmit={abrir}>
          {whats.length === 0 ? (
            <p className="modal-note">
              Nenhum WhatsApp conectado agora. Pareie um em Configurações → WhatsApp para iniciar conversas.
            </p>
          ) : (
            <>
              {/* Só aparece quando há escolha a fazer. Com uma conta, perguntar "de qual número?"
                  é uma pergunta com uma resposta só. */}
              {whats.length > 1 && (
                <label className="fld">
                  <span>Enviar pelo WhatsApp</span>
                  <select value={conta} onChange={(e) => setContaId(e.target.value)}>
                    {whats.map((c) => (
                      <option key={c.id} value={c.id}>{c.displayName}</option>
                    ))}
                  </select>
                </label>
              )}

              <label className="fld">
                <span>Número com DDD</span>
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  placeholder="(19) 99249-0527"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoFocus
                />
              </label>

              <label className="fld">
                <span>Nome (opcional)</span>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Como aparece na lista" />
              </label>

              <p className="modal-note">
                Nada é enviado ao abrir. Se já existe conversa com este número, ela é que abre.
              </p>
            </>
          )}

          {erro && <div className="modal-err">{erro}</div>}
          {aviso && <div className="modal-note warn">{aviso}</div>}

          <div className="modal-f">
            <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
              {aviso ? 'Fechar' : 'Cancelar'}
            </button>
            {!aviso && (
              <button type="submit" className="btn pri" disabled={busy || !conta || !phone.trim()}>
                {busy ? 'Verificando número…' : 'Abrir conversa'}
              </button>
            )}
          </div>
        </form>
      </div>
    </>
  )
}
